import sortUnitsByTitle from 'common/helpers/sort-units-by-title'
import Curriculum from 'client/js/models/curriculum.js'
import {eachLimit} from 'common/helpers/async-tools.js'

function getNewStateObj() {
   return {
      units: [],
      completedUnits: [],
      GeneralElectiveLevel100Alert: false,
   }
}

const getters = {

   // returns currently selected curriculum items
   getSelectedCurriculumItems: state => {
      return state.units || [];
   },

   // returns currently selected unit codes
   getSelectedUnitCodes: state => {
      var selectedItems = state.units || [];

      return selectedItems.map(x => x.relationship.code);
   },
   // returns currently selected completed units
   getCompletedUnits: state => state.completedUnits,

   // return currently selected curriculum items ins a specific location
   getSelectedCurriculumItemByLocation: (state, getters) => (location) => {
      return getters['getSelectedCurriculumItems']
         .filter(item => item.location.startsWith(location));
   },

   // returns total credits based on selected items.
   // countDuplicate indicates whether duplicate units will be counted towards the total credit or not
   getTotalCredits: (state, getters) => (location, countDuplicate = true, countCompleted = true) => {
      const seenItems = [];
      var completedUnits = getters['getCompletedUnits']

      return getters['getSelectedCurriculumItems'].reduce((sum, item) => {
         if (item.relationship.type === 'Unit' &&
            item.location.startsWith(location) &&
            (countDuplicate || seenItems.indexOf(item.id) < 0) && //if we are adding duplicates, or we've never seen this before.
            (countCompleted || completedUnits.indexOf(item.code) < 0)
         ) {
            //add it to the sum
            sum += parseFloat(item.relationship.credits) || 0;
         }
         seenItems.push(item.id)
         return sum
      }, 0);
   },

   isLevel100: state => (id, verticalGrouping, doTotals) => {
      if (!doTotals
      && verticalGrouping != 'General Elective') return false
      const location = id.search(/\w\d/)+1
      if (location <= 2) {
         return false
      }
      const is100LevelUnit = id.substring(location, location + 1) == '1'
      return is100LevelUnit
   },

   // returns the unit level based on the unit code
   getUnitLevel: (state, getters) => (id) => {
      const location = id.search(/\w\d/) + 1
      if (location <= 2) {
         return '0' // No level match, but we do a string match, so default to '0'
      }
      return id.substring(location, location + 1)
   },

   getLevel100Credits: (state, getters) => (selectedItems) => {
      const seenItems = []
      var filtered = selectedItems.filter(x => getters.isLevel100(x.relationship.id, x.verticalGrouping, true))
      var reducedTotal = filtered.reduce((sum, item) => {
         if (item.relationship.type === 'Unit' &&
             seenItems.indexOf(item.id) < 0)
         {
            //add it to the sum
            sum += parseFloat(item.relationship.credits) || 0;
         }
         seenItems.push(item.id)
         return sum
      }, 0)

      return reducedTotal;
   },

   getGeneralElectiveLevel100Alert: state => state.GeneralElectiveLevel100Alert,

   getUnallocatedUnits: (state, getters) => {
      return getters['getSelectedCurriculumItems']
         .filter(
            (item) =>
            item.relationship.type === 'Unit' &&
            getters['getAllocatedUnitIds'].indexOf(item.relationship.id) < 0 &&
            !getters['getCompletedUnits'].includes(item.relationship.code)
         )
         .sort(sortUnitsByTitle)
         .reduce((total, current) => {
            // Unique based on relationship code
            if (!total.map((x) => x.relationship.code).includes(current.relationship.code)) {
            total.push(current)
            }
            return total
         }, [])
   },
}

const mutations = {
   // set selected curriculum items
   setCurriculumItems(state, units) {
      state.units = units;
   },

   // set completed units
   setCompletedUnits: (state, completedUnits) => {
      state.completedUnits = completedUnits;
   },

   setGeneralElectiveLevel100Alert: (state, alert) => state.GeneralElectiveLevel100Alert = alert,

   resetState: (state) => {
      Object.assign(state, getNewStateObj())
   }
}

const actions = {

   async recursiveLoadAndSelectUnits({state, commit, dispatch}, {parentContainer, selectedUnits, location, firstContainerName, incDrafts}) {
      //CURRENT LOCATION!

      let relationshipsToLoad = []


      if(parentContainer.conditionSet?.length > 0)
      {
         //Highlander rule for condition sets.
         //there is only ever one condition set...

         //load the condition set.
         const response = await Curriculum.getCachedConditionSet(parentContainer.cl_id, incDrafts, true)
         //loop through all units in condition set (relationships)
         for (let itemIndex in response?.items)
         {
            const relationship = response.items[itemIndex];
            const relationshipLocation = location + '>' + relationship.code

            //only load the item IF we are a selected relationship!
            if (selectedUnits.some((selectedUnit) => selectedUnit == relationshipLocation))
            {
               relationship.virtual = true;
               relationshipsToLoad.push(relationship);
            }
         }
      }

      if (parentContainer.relationship?.length > 0)
      {
         for (let index in parentContainer.relationship)
         {
            const relationship = parentContainer.relationship[index]
            const relationshipLocation =  location + '>' + relationship.code

            //only load the item IF we are a selected relationship!
            if (selectedUnits.some((selectedUnit) => selectedUnit == relationshipLocation))
            {
               relationshipsToLoad.push(relationship);
            }
         }

      }

      // Load relationship data in parallel
      await eachLimit(relationshipsToLoad, async (relationship, index) => {
         let item = null
         let version = relationship.versionNumber
         console.log('recursiveLoadAndSelectUnits', relationship.name, relationship.type, relationship.versionNumber, relationship.courseLoopVersionNumber, incDrafts)
         // TODO: Pass through includeDraftVersions flag (for SavedCoursePlans context)
         switch (relationship.type) {
            case 'Unit':
               if (!relationship.unit)
               {
                  relationship.unit = await Curriculum.getUnit(relationship.code, relationship.version)
               }

               item = relationship.unit;

              break
            case 'Major':
              item = await Curriculum.getMajor(relationship.code, relationship.courseLoopVersionNumber, incDrafts)
              break
            case 'Co-Major':
              item = await Curriculum.getCoMajor(relationship.code, relationship.courseLoopVersionNumber, incDrafts)
              break
            case 'Minor':
              item = await Curriculum.getMinor(relationship.code, relationship.courseLoopVersionNumber, incDrafts)
              break
            default:
          }


         // Add items to Journey's curriculum items
         dispatch("addCurriculumItem", {
            firstLevelTitle: firstContainerName,
            relationship: relationship,
            location: location +'>'+ relationship.code,
            verticalGrouping: parentContainer.verticalGrouping // this.verticalGrouping
         })

         //attach relationships / structure
         if (relationship.virtual && location) {
            // attach relationships to parent
            dispatch("courses/addRelationships", {
               location: location,
               relationships: [relationship]
            }, { root: true })
         }

         if(item?.structure?.length > 0)
         {
            if (item?.structure) {
               // update structure
               dispatch("courses/attachStructure", {
                  location: location +'>'+ relationship.code,
                  structure: item?.structure[0]
               }, { root: true })
            }

            for(let cindex in item.structure[0].containers)
            {
               const container = item.structure[0].containers[cindex]
               await dispatch('recursiveLoadAndSelectUnits', { parentContainer: container, selectedUnits, location: location +'>'+ relationship.code + ">" + container.title+'-'+container.order, firstContainerName: firstContainerName, incDrafts})
            };
          }

      })

      if (parentContainer.container?.length > 0)
      {
         for(let index in parentContainer.container)
         {
            const container = parentContainer.container[index]
            await dispatch('recursiveLoadAndSelectUnits', { parentContainer: container, selectedUnits, location: location+'>'+container.title+'-'+container.order, firstContainerName: firstContainerName, incDrafts})
         }
      }
   },

   async validateAndLoadUnits({state, commit, getters, dispatch, rootGetters}, {selectedUnits, completedUnits, incDrafts}) {
      // Reset selected units to repopulate
      commit('setCurriculumItems', [])

      //assign the completed units to the completed units list
      if (completedUnits && completedUnits.length > 0)
         commit('setCompletedUnits', completedUnits);

      //load and select the units
      let loadedCourse = rootGetters["courses/getSelectedCourse"]

      for (let index in loadedCourse.structure[0].containers)
      {
         const container = loadedCourse.structure[0].containers[index]
         await dispatch('recursiveLoadAndSelectUnits', { parentContainer: container, selectedUnits, location: 'root>'+container.title+'-'+container.order, firstContainerName: container.title, incDrafts});
      }

      const units = getters['getSelectedCurriculumItems']
      let errorList = []
      let unitList = units.map((unit) => unit.location)
      selectedUnits.forEach((selectedUnit) => {
         if (!unitList.includes(selectedUnit)) {
            errorList.push(selectedUnit)
         }
      })

      if (errorList.length > 0) {
         commit('savedCourse/setMissingUnits', errorList, { root: true });
         throw errorList
      }

   },

   // add curriculum items provided to the list
   addCurriculumItem({ commit, getters }, item) {
      const currentItems = getters['getSelectedCurriculumItems']

      currentItems.push({
         id: item.relationship.id,
         code: item.relationship.code,
         ...item
      });

      // Update status of the General Electives Level-100 units
      const isLevel = getters.isLevel100(item.relationship.id, item.verticalGrouping)
      if (isLevel && !getters.getGeneralElectiveLevel100Alert){
         const level100CreditsTotal = getters.getLevel100Credits(currentItems);
         if (level100CreditsTotal > 30) {
            commit('setGeneralElectiveLevel100Alert', true)
         }
      }

      commit("setCurriculumItems", currentItems);
   },
   // remove curriculum item based on location of item provided
   removeCurriculumItem({ commit, getters, dispatch }, item) {
      const currentItems = getters['getSelectedCurriculumItems'];
      const removeIndex = currentItems.findIndex(currentItem => currentItem.location === item.location);

      if (removeIndex >= 0) {
         const updatedItems = [...currentItems];
         updatedItems.splice(removeIndex, 1);

         // Update status of the General Electives Level-100 units
         if (getters.getGeneralElectiveLevel100Alert){
            const level100CreditsTotal = getters.getLevel100Credits(currentItems);
            if (level100CreditsTotal <= 30) {
               commit('setGeneralElectiveLevel100Alert', false)
            }
         }

         commit("setCurriculumItems", updatedItems);

         if (item.recursive) {
            const children = updatedItems.filter(currentItem => currentItem.location.startsWith(item.location))
            if (children && children.length) {
               children.forEach(child => {
                  dispatch("removeCurriculumItem", child)
               })
            }
         }
      }
   },
   // update unit completion
   updateUnitCompletion({ commit, getters }, { code, completed }) {
      const currentCompleted = getters['getCompletedUnits']
      if (completed) {
         if (!currentCompleted.includes(code)) {
            currentCompleted.push(code)
            commit("setCompletedUnits", currentCompleted)
         }
      }
      else {
         if (currentCompleted.includes(code)) {
            const index = currentCompleted.findIndex(item => item === code)
            currentCompleted.splice(index, 1)
            commit("setCompletedUnits", currentCompleted)
         }
      }
   },
}

export default {
   namespaced: true,
   state: getNewStateObj(),
   getters,
   actions,
   mutations,
   getNewStateObj
}
